import { BrandingInfo } from '../models/branding.model';
import { PmAccount } from '../dto/pm-account';

/**
 * Opt-in switch that skips the lease-restriction check, so the PMA flow can be
 * clicked through without a lease-matching identity (no user_id, tcode or email
 * the backend can match against a lease).
 *
 *   on:  localStorage.setItem('gc_debug_disable_lease_restriction', 'true')
 *   off: localStorage.removeItem('gc_debug_disable_lease_restriction')
 *
 * then reload.
 *
 * ENABLED IN PRODUCTION TOO, deliberately. The single guarantee that makes that
 * acceptable is the one below: without the key, this returns false and the flow
 * behaves EXACTLY as it did before this existed — same code path, same call,
 * same result. Every branch that is not an explicit opt-in returns false:
 *
 *   - key absent                       -> false
 *   - key set to anything but 'true'   -> false (strict equality, no coercion)
 *   - server-side render, no localStorage -> false
 *   - localStorage throws (private mode, storage blocked) -> false
 *
 * So the risk is confined to whoever sets it, per browser. It is not a
 * server-side control: the key is a plain string in the shipped bundle, so
 * treat this as a convenience for people who already have access to the flow,
 * NOT as something that keeps anyone out. If the check ever needs to be
 * enforceable, that belongs at /policies/external/upload on submit — this
 * function only decides whether the client asks
 * /user_matching/lease_restricted at all.
 *
 * Warns on every bypass on purpose: a silently disabled compliance check is how
 * one ends up left on by accident.
 */
export const DEBUG_DISABLE_LEASE_RESTRICTION_KEY = 'gc_debug_disable_lease_restriction';

export function leaseRestrictionDisabledForDebug(): boolean {
  try {
    // Guarded: there is no localStorage during SSR, and a browser can refuse it.
    return typeof localStorage !== 'undefined'
      && localStorage.getItem(DEBUG_DISABLE_LEASE_RESTRICTION_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Normalizes `lease_restriction_community_ids` from branding attributes (array or JSON string like `'[12500]'`).
 */
function getLeaseRestrictionCommunityIds(brandingInfo: BrandingInfo): number[] {
  const raw = brandingInfo.profile_attributes?.find(
    attr => attr.name === 'lease_restriction_community_ids'
  )?.value;

  if (Array.isArray(raw)) {
    return raw;
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return [];
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

/**
 * Determines whether to run the lease restriction check based on branding settings
 * and the selected PM account (when nonpreferred_account_selection mode is enabled).
 *
 * - When lease_restricted_user_matching is true (branding or selected PM account): the check
 *   applies to all communities under this flow; branding profile `lease_restriction_community_ids`
 *   does not narrow scope.
 * - When lease_restricted_user_matching is false: the check runs only for communities whose IDs
 *   appear in branding profile attribute `lease_restriction_community_ids` (per-site / per-branding).
 *
 * @param brandingInfo - The branding info containing lease_restricted_user_matching flag
 * @param nonpreferredAccountSelection - Whether nonpreferred account selection mode is enabled
 * @param selectedPmAccount - The selected PM account (if any) when in nonpreferred mode
 * @param communityId - Selected community ID; required when using community-id-only mode
 * @returns Whether the lease restriction check should be performed
 */
export function shouldRunLeaseRestrictionCheck(
  brandingInfo: BrandingInfo | null,
  nonpreferredAccountSelection: boolean,
  selectedPmAccount: PmAccount | null,
  communityId?: number
): boolean {
  if (leaseRestrictionDisabledForDebug()) {
    console.warn(
      '[debug] lease-restriction check BYPASSED via '
      + `localStorage['${DEBUG_DISABLE_LEASE_RESTRICTION_KEY}']. `
      + 'Remove that key to restore the real check.',
    );
    return false;
  }

  if (!brandingInfo) {
    return false;
  }

  let leaseRestrictedUserMatching: boolean;

  if (nonpreferredAccountSelection && selectedPmAccount) {
    leaseRestrictedUserMatching = selectedPmAccount.lease_restricted_user_matching === true;
  } else {
    leaseRestrictedUserMatching = brandingInfo.lease_restricted_user_matching === true;
  }

  if (leaseRestrictedUserMatching) {
    return true;
  }

  const communityIds = getLeaseRestrictionCommunityIds(brandingInfo);

  if (communityIds.length === 0) {
    return false;
  }

  return (
    communityId != null &&
    communityIds.some(id => id === communityId || Number(id) === Number(communityId))
  );
}

const DEFAULT_LEASE_RESTRICTION_ERROR = "Oops! Something went wrong. We couldn't retrieve the information. Please try again later.";

/**
 * Resolves the lease restriction error message with the following priority:
 * 1. Branding profile attribute `lease_restriction_error_message` (partner whitelabel, supports HTML)
 * 2. Backend error from the API response
 * 3. Hardcoded fallback
 */
export function getLeaseRestrictionErrorMessage(
  brandingInfo: BrandingInfo | null,
  backendError?: string
): string {
  const brandingMessage = brandingInfo?.profile_attributes
    ?.find(attr => attr.name === 'lease_restriction_error_message')?.value;

  return brandingMessage || backendError || DEFAULT_LEASE_RESTRICTION_ERROR;
}
